// ffmpeg.wasm wrapper for the web Builder upload flow.
//
// Guarantees the output is iOS-playable H.264 + AAC mp4
// regardless of the input format. Architecture:
//
//   1. Load ffmpeg.wasm (cached after first call this
//      session)
//   2. Run `ffmpeg -i input` with no output specifier —
//      ffmpeg prints stream info to stderr, then exits
//      with code 1 ("At least one output file must be
//      specified"). Catch the throw, parse the stderr
//      capture for the video codec.
//   3. Always transcode with explicit iOS-safe constraints:
//      `-c:v libx264 -profile:v high -level 4.1
//       -pix_fmt yuv420p -preset veryfast -crf 23
//       -c:a aac -b:a 128k -movflags +faststart`.
//      ~20-30s for a 90s clip on a modern Mac.
//   4. If pass-1 output still exceeds the server size cap,
//      a tighter pass 2 (480p, crf 30) runs as the
//      fallback.
//
// We use ffmpeg itself as the codec probe — it can read
// every container/codec, so we never need a hand-rolled
// MP4 parser. The decision of what works (h264) is
// explicit; everything else gets normalised.
//
// Single-threaded core — no SharedArrayBuffer, no
// COOP/COEP headers needed on the SPA. Multi-threaded
// would be ~2-3x faster but requires server-side header
// changes; deferred.

import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';

// Target max output size after transcode. Matches the
// server's MAX_VIDEO_SIZE_BYTES at videos.ts:57 — keep
// these two values in sync. If pass-1 (720p/crf 23) output
// exceeds this, pass-2 (480p/crf 30) runs as the fallback.
const MAX_OUTPUT_BYTES = 500 * 1024 * 1024;

// Load @ffmpeg/core from jsdelivr at runtime instead of
// self-hosting the wasm via Vite.
//
// Why not self-host: Convex static-hosting choked on the
// ~25MB ffmpeg-core wasm — the asset returned HTTP 200 but
// the HTTP/2 stream errored mid-transfer
// (ERR_HTTP2_PROTOCOL_ERROR). The hosting layer is built for
// SPA JS/CSS bundles, not for serving large binary assets,
// and Cloudflare's edge gzip on the .wasm appears to be the
// straw that breaks it. jsdelivr serves the exact same npm
// artifact (registry-mirrored, immutable per version,
// content-hashed CDN, broad global PoP, proper CORS for
// blob: conversion) and handles 25MB binaries without
// breaking a sweat.
//
// Must be the ESM build (`dist/esm/ffmpeg-core.js`), NOT
// UMD. @ffmpeg/ffmpeg@0.12.x creates a module-typed worker;
// `importScripts` doesn't exist in module workers, so the
// worker falls through to `(await import(coreURL)).default`
// — which only works on an ESM file with a default export.
//
// Version pinned by exact match against the @ffmpeg/core
// devDep in src/web/package.json. Bump both together.
const FFMPEG_CORE_VERSION = '0.12.10';
const FFMPEG_CORE_BASE =
  `https://cdn.jsdelivr.net/npm/@ffmpeg/core@${FFMPEG_CORE_VERSION}/dist/esm`;

let ffmpegInstance: FFmpeg | null = null;

async function loadFfmpeg(): Promise<FFmpeg> {
  if (ffmpegInstance) return ffmpegInstance;
  const ffmpeg = new FFmpeg();
  // Pre-fetch JS + wasm on the main thread via fetch
  // (toBlobURL → blob: URLs), then hand those to the worker.
  // The worker thus reads from same-origin blob URLs and
  // never makes a network request itself — which dodges the
  // ffmpeg-core emscripten XHR path that fails when run
  // from a module worker against some hosts.
  const [coreBlobURL, wasmBlobURL] = await Promise.all([
    toBlobURL(`${FFMPEG_CORE_BASE}/ffmpeg-core.js`, 'text/javascript'),
    toBlobURL(`${FFMPEG_CORE_BASE}/ffmpeg-core.wasm`, 'application/wasm'),
  ]);
  await ffmpeg.load({ coreURL: coreBlobURL, wasmURL: wasmBlobURL });
  ffmpegInstance = ffmpeg;
  return ffmpeg;
}

/**
 * Normalise any picked video file to iOS-playable
 * H.264 + AAC mp4. H.264 sources are remuxed only
 * (fast); anything else gets transcoded.
 *
 * `onProgress` receives integer percent values 0..100.
 * For the remux branch the progress is essentially
 * instant (callback may not fire at all); for the
 * transcode branch it climbs as ffmpeg encodes.
 *
 * On failure, throws an Error whose `.message` includes
 * the last ~25 lines of ffmpeg stderr — sufficient to
 * diagnose missing encoders, OOM, malformed input, etc.
 */
export async function ensureH264(
  file: File,
  onProgress: (pct: number) => void,
): Promise<File> {
  const ffmpeg = await loadFfmpeg();

  // Capture ffmpeg stderr. The log handler also sniffs
  // for the video codec line during the probe pass —
  // `Stream #0:0[0x1](und): Video: h264 (...)`.
  const logBuf: string[] = [];
  let detectedVideoCodec: string | null = null;
  const logHandler = ({ message }: { message: string }) => {
    logBuf.push(message);
    if (logBuf.length > 200) logBuf.shift();
    if (!detectedVideoCodec) {
      const m = message.match(/Stream #\d+:\d+.*: Video: (\w+)/);
      if (m) detectedVideoCodec = m[1].toLowerCase();
    }
  };
  ffmpeg.on('log', logHandler);

  const progressHandler = ({ progress }: { progress: number }) => {
    onProgress(Math.max(0, Math.min(100, Math.round(progress * 100))));
  };
  ffmpeg.on('progress', progressHandler);

  try {
    // Diagnostic logging. Strip once SIZE_TOO_LARGE on the
    // server is decisively closed.
    console.info('[video] source', {
      size: file.size,
      type: file.type,
      name: file.name,
    });

    await ffmpeg.writeFile('in', await fetchFile(file));

    // Probe — running with no output specifier makes
    // ffmpeg dump stream info to stderr then exit with
    // code 1. Catch the expected throw and read the
    // captured log instead.
    await ffmpeg.exec(['-i', 'in']).catch(() => {});

    // Bail early on AV1. @ffmpeg/core@0.12.10's
    // native AV1 decoder requires hardware acceleration
    // which isn't available in the wasm context; the actual
    // transcode fails with cryptic "Function not implemented"
    // / "Missing Sequence Header" errors. Detect at probe
    // time and surface a clear codec-specific error instead.
    // YouTube uploads + Pixel screen recordings hit this in
    // practice.
    if (detectedVideoCodec === 'av1') {
      throw new Error(
        'UNSUPPORTED_CODEC_AV1: @ffmpeg/core does not include ' +
          'a working software AV1 decoder',
      );
    }

    // Always transcode (no H.264 fast-remux). The remux
    // fast path byte-copied the source, which kept
    // already-H.264 4K screen recordings at their original
    // size and routinely tripped the server's 500MB cap.
    // Transcoding every upload through libx264 gives us a
    // resolution cap (via the scale filter below) and a
    // predictable output size (~25-35MB for a 90s clip @
    // 720p). The codec-detection probe above still runs —
    // it's used to enrich the error message on failure.
    //
    // Two-pass adaptive size shrink:
    //   Pass 1: 720p + crf 23 + maxrate 30M (typical 25-35MB)
    //   Pass 2: 480p + crf 30 + maxrate 10M (max ~113MB) — only
    //           runs if pass 1's output still exceeds the server
    //           cap, which requires extremely high-bitrate /
    //           complex source content. Vanishingly rare in
    //           practice but covers the "what if" cleanly.
    //
    // Scale filter — `1280:1280` is a bounding box, NOT
    // forced output dimensions. `force_original_aspect_ratio
    // =decrease` preserves the source's aspect ratio AND
    // only downscales (sources already ≤1280 on the long
    // edge pass through untouched). Works for landscape +
    // portrait — a 4K landscape clip becomes 1280×720, a
    // 4K portrait clip becomes 720×1280. Matches the mobile
    // iOS picker preset (H264_1280x720), so platform parity
    // holds.
    //
    // Encode args otherwise: H.264 high profile + Level 4.1 +
    // yuv420p is the AVPlayer-compatible combo for iOS. AAC
    // stereo for audio. +faststart so the viewer can
    // byte-range scrub without buffering the whole file.
    const firstPassArgs: string[] = [
      '-i', 'in',
      // Named-args form for the scale filter. The hybrid
      // `scale=W:H:force_original_aspect_ratio=decrease`
      // appeared to be silently rejected by @ffmpeg/core@0.12.10,
      // producing 0-byte output. Fully-named args work everywhere.
      '-vf', 'scale=w=1280:h=1280:force_original_aspect_ratio=decrease',
      '-c:v', 'libx264',
      '-profile:v', 'high',
      '-level', '4.1',
      '-pix_fmt', 'yuv420p',
      '-preset', 'veryfast',
      '-crf', '23',
      '-maxrate', '30M',
      '-bufsize', '60M',
      '-c:a', 'aac',
      '-b:a', '128k',
      '-movflags', '+faststart',
      'out.mp4',
    ];

    // Capture the exit code. ffmpeg.exec resolves with the
    // exit code on completion; it does NOT throw
    // on non-zero. Without this check a failed transcode
    // silently propagates a 0-byte file.
    const exit1 = await ffmpeg.exec(firstPassArgs);
    if (exit1 !== 0) {
      throw new Error(
        `ffmpeg pass 1 exit ${exit1}. log tail:\n${logBuf.slice(-25).join('\n')}`,
      );
    }

    let data = await ffmpeg.readFile('out.mp4');
    let bytes = data as Uint8Array;

    // Diagnostic.
    console.info('[video] pass1 out', { bytes: bytes.byteLength });

    // Belt-and-braces empty-output check. Catches
    // the case where ffmpeg exits 0 but never wrote out.mp4
    // (or wrote 0 bytes), which the byteLength > cap check
    // below would have allowed through.
    if (bytes.byteLength === 0) {
      throw new Error(
        `ffmpeg pass 1 produced 0 bytes. log tail:\n${logBuf.slice(-25).join('\n')}`,
      );
    }

    // Second-pass shrink — only runs if pass 1's output
    // exceeds the server cap. Tighter resolution box (854 =
    // standard "480p widescreen" long edge), higher crf,
    // lower maxrate. Worst-case output: 90s × 10Mbps ≈
    // 113MB.
    if (bytes.byteLength > MAX_OUTPUT_BYTES) {
      await ffmpeg.deleteFile('out.mp4').catch(() => {});
      const secondPassArgs: string[] = [
        '-i', 'in',
        '-vf', 'scale=w=854:h=854:force_original_aspect_ratio=decrease',
        '-c:v', 'libx264',
        '-profile:v', 'high',
        '-level', '4.1',
        '-pix_fmt', 'yuv420p',
        '-preset', 'veryfast',
        '-crf', '30',
        '-maxrate', '10M',
        '-bufsize', '20M',
        '-c:a', 'aac',
        '-b:a', '96k',
        '-movflags', '+faststart',
        'out.mp4',
      ];
      const exit2 = await ffmpeg.exec(secondPassArgs);
      if (exit2 !== 0) {
        throw new Error(
          `ffmpeg pass 2 exit ${exit2}. log tail:\n${logBuf.slice(-25).join('\n')}`,
        );
      }
      data = await ffmpeg.readFile('out.mp4');
      bytes = data as Uint8Array;

      // Diagnostic.
      console.info('[video] pass2 out', { bytes: bytes.byteLength });

      // Empty-output check for pass 2 too.
      if (bytes.byteLength === 0) {
        throw new Error(
          `ffmpeg pass 2 produced 0 bytes. log tail:\n${logBuf.slice(-25).join('\n')}`,
        );
      }

      if (bytes.byteLength > MAX_OUTPUT_BYTES) {
        // Two failed passes — pathological source. Throw so
        // the caller surfaces "Couldn't prepare this video
        // for upload" instead of letting the server reject.
        await ffmpeg.deleteFile('in').catch(() => {});
        await ffmpeg.deleteFile('out.mp4').catch(() => {});
        throw new Error(
          `Video too complex to compress under ${Math.round(MAX_OUTPUT_BYTES / 1024 / 1024)}MB (output: ${Math.round(bytes.byteLength / 1024 / 1024)}MB)`,
        );
      }
    }

    await ffmpeg.deleteFile('in').catch(() => {});
    await ffmpeg.deleteFile('out.mp4').catch(() => {});

    // Wrap as a new File. Drop any extension on the
    // input name; output is always .mp4 / video/mp4 so
    // the existing presign + PUT flow doesn't branch.
    const out = new File(
      [bytes],
      file.name.replace(/\.\w+$/, '') + '.mp4',
      { type: 'video/mp4' },
    );

    // Diagnostic + defensive guard. If File.size diverges
    // from bytes.byteLength OR somehow exceeds the
    // cap after a "passing" check, this branch fires loudly
    // instead of letting the server reject. Should be
    // unreachable; if it ever fires we have a real bug.
    console.info('[video] return', {
      size: out.size,
      byteLength: bytes.byteLength,
    });
    // Surface the ffmpeg log tail on the success path too,
    // not just on throws. Useful for confirming
    // the scale filter actually applied (look for `Stream
    // mapping` and the configured filtergraph in the tail).
    console.info('[video] ffmpeg log tail', {
      tail: logBuf.slice(-25).join('\n'),
    });
    if (out.size > MAX_OUTPUT_BYTES) {
      const tail = logBuf.slice(-25).join('\n');
      throw new Error(
        `Internal: ensureH264 output ${out.size}B exceeded cap ` +
          `${MAX_OUTPUT_BYTES}B — this should be unreachable. ` +
          `bytes.byteLength=${bytes.byteLength}. ffmpeg log tail:\n${tail}`,
      );
    }
    return out;
  } catch (err) {
    // Enrich with ffmpeg stderr tail so callers can
    // show / log a useful diagnostic. Common shapes
    // we expect to see:
    //   - "Unknown encoder 'libx264'"  → LGPL core
    //     doesn't have it; switch to libopenh264
    //   - "Out of memory"               → wasm OOM
    //   - "Invalid data found"          → corrupt input
    const tail = logBuf.slice(-25).join('\n');
    const enriched = new Error(
      `ffmpeg failed (input codec: ${detectedVideoCodec ?? 'unknown'}):\n` +
        `${String(err)}\n` +
        `--- ffmpeg log tail ---\n${tail}`,
    );
    (enriched as Error & { ffmpegLog?: string[] }).ffmpegLog = logBuf;
    throw enriched;
  } finally {
    ffmpeg.off('log', logHandler);
    ffmpeg.off('progress', progressHandler);
  }
}

// Back-compat alias for callers that still import the
// old name. ensureH264 is the canonical export.
export const transcodeToH264 = ensureH264;
